'use client';

import { useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';

interface ScrollTrackingProps {
  pageName?: string;
  variant?: string;
  language?: string;
}

export default function ScrollTracking({
  pageName = 'unknown',
  variant,
  language,
}: ScrollTrackingProps) {
  const posthog = usePostHog();
  const hasTracked25Ref = useRef(false);
  const hasTracked75Ref = useRef(false);
  const hasTrackedBottomRef = useRef(false);

  useEffect(() => {
    if (!posthog) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;

      // Track 25% scroll (hero engagement)
      if (!hasTracked25Ref.current && scrollPercent >= 25) {
        hasTracked25Ref.current = true;
        posthog.capture('page_scroll_25_percent', {
          page_name: pageName,
          scroll_percent: Math.round(scrollPercent),
          variant: variant,
          language: language,
        });

        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog: 25% scroll tracked for', pageName);
        }
      }

      // Track 75% scroll (deep engagement)
      if (!hasTracked75Ref.current && scrollPercent >= 75) {
        hasTracked75Ref.current = true;
        posthog.capture('page_scroll_75_percent', {
          page_name: pageName,
          scroll_percent: Math.round(scrollPercent),
          variant: variant,
          language: language,
        });

        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog: 75% scroll tracked for', pageName);
        }
      }

      // Track bottom of page (maximum engagement)
      if (!hasTrackedBottomRef.current && scrollPercent >= 95) {
        hasTrackedBottomRef.current = true;
        posthog.capture('page_scroll_bottom', {
          page_name: pageName,
          scroll_percent: Math.round(scrollPercent),
          variant: variant,
          language: language,
        });

        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog: Bottom scroll tracked for', pageName);
        }
      }
    };

    // Track initial page view
    posthog.capture('page_view', {
      page_name: pageName,
      variant: variant,
      language: language,
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('PostHog: Page view tracked for', pageName);
    }

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [posthog, pageName, variant, language]);

  return null; // This component doesn't render anything
}
